import React, { useEffect } from 'react';
import { X } from 'lucide-react';

const TUTORIAL_YOUTUBE_ID = 'S0xUx6WlAXs';
const SESSION_STORAGE_KEY = 'tutorial_shown_this_session';

export interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si true, se mostró automáticamente al iniciar sesión (muestra opción "No volver a mostrar") */
  wasAutoShown?: boolean;
}

export const TutorialModal: React.FC<TutorialModalProps> = ({
  isOpen,
  onClose,
  wasAutoShown = false,
}) => {
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleClose = () => {
    if (wasAutoShown && dontShowAgain) {
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
      } catch (_) {}
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
    >
      <div
        className="relative w-full max-w-3xl bg-[#1F2937] rounded-2xl shadow-2xl overflow-hidden border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-[#111827]">
          <h2 id="tutorial-title" className="text-lg font-semibold text-white">
            Tutorial — Topógrafo
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>

        {/* Video 16:9 */}
        <div className="relative w-full aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${TUTORIAL_YOUTUBE_ID}?rel=0`}
            title="Tutorial Topógrafo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>

        {/* Pie: opción "No volver a mostrar" solo si se abrió automáticamente */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-700 bg-[#111827]">
          {wasAutoShown ? (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-400 hover:text-gray-300">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <span>No volver a mostrar esta sesión</span>
            </label>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;

/** Clave para que el layout sepa si debe auto-mostrar el tutorial esta sesión. */
export function shouldShowTutorialThisSession(): boolean {
  try {
    return !sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return true;
  }
}
